import React from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Box,
	RadioGroup,
	FormControlLabel,
	Radio,
	Alert,
	Stack
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';

const DeleteConfirmationDialog = ({
	open,
	onClose,
	onConfirm,
	report,
	isLoading = false
}) => {
	const [deleteOption, setDeleteOption] = React.useState('self');
	const isShared = report?.shareType === 'shared';

	const handleConfirm = () => {
		onConfirm(deleteOption === 'all');
	};

	const handleClose = () => {
		setDeleteOption('self');
		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
		>
			<DialogTitle sx={{ 
				display: 'flex', 
				alignItems: 'center', 
				gap: 1,
				color: 'error.main',
				borderBottom: '1px solid #e0e0e0'
			}}>
				<WarningIcon color="error" />
				Delete Report Subscription
			</DialogTitle>
			
			<DialogContent sx={{ pt: 2 }}>
				<Stack spacing={2}>
					<Alert severity="warning" sx={{ mb: 2 }}>
						<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
							Are you sure you want to delete "{report?.name}"?
						</Typography>
						<Typography variant="body2" sx={{ mt: 0.5 }}>
							This action cannot be undone.
						</Typography>
					</Alert>

					{isShared && (
						<Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
							<Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#333' }}>
								Delete Options
							</Typography>
							<Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
								This report has been shared with others. Choose how you want to delete it:
							</Typography>
							
							<RadioGroup
								value={deleteOption}
								onChange={(e) => setDeleteOption(e.target.value)}
							>
								<FormControlLabel
									value="self"
									control={<Radio />}
									label={
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<PersonIcon sx={{ fontSize: 20, color: '#2196f3' }} />
											<Box>
												<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
													Delete for me only
												</Typography>
												<Typography variant="caption" sx={{ color: '#666' }}>
													The report will be removed from your account but remain available to users you've shared it with.
												</Typography>
											</Box>
										</Box>
									}
									sx={{ mb: 1 }}
								/>
								<FormControlLabel
									value="all"
									control={<Radio />}
									label={
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<GroupIcon sx={{ fontSize: 20, color: '#f44336' }} />
											<Box>
												<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
													Delete for everyone
												</Typography>
												<Typography variant="caption" sx={{ color: '#666' }}>
													The report will be completely removed and no longer available to anyone, including users you've shared it with.
												</Typography>
											</Box>
										</Box>
									}
								/>
							</RadioGroup>
						</Box>
					)}

					{!isShared && (
						<Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
							<Typography variant="body2" sx={{ color: '#666' }}>
								This will permanently delete the report subscription from your account.
							</Typography>
						</Box>
					)}
				</Stack>
			</DialogContent>
			
			<DialogActions sx={{ p: 2, gap: 1 }}>
				<Button 
					onClick={handleClose}
					disabled={isLoading}
					variant="outlined"
				>
					Cancel
				</Button>
				<Button
					onClick={handleConfirm}
					disabled={isLoading}
					variant="contained"
					color="error"
					startIcon={<DeleteForeverIcon />}
				>
					{isLoading ? 'Deleting...' : 'Delete'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default DeleteConfirmationDialog; 